import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Calculator, HardHat, Package, Wrench, FileEdit, Building2, Scale, TrendingUp,
  Settings, Lock
} from 'lucide-react';
import { EstimateTab } from '../../components/financial-os/EstimateTab';
import { LaborTab } from '../../components/financial-os/LaborTab';
import { MaterialsTab } from '../../components/financial-os/MaterialsTab';
import { EquipmentTab } from '../../components/financial-os/EquipmentTab';
import { ChangeOrdersTab } from '../../components/financial-os/ChangeOrdersTab';
import { OverheadTab } from '../../components/financial-os/OverheadTab';
import { BurdenRateTab } from '../../components/financial-os/BurdenRateTab';
import { ProfitTab } from '../../components/financial-os/ProfitTab';
import { VisibilityModal } from '../../components/financial-os/VisibilityModal';

const ALL_TABS = [
  { key: 'estimate', name: 'Project Estimate', icon: Calculator, jobScoped: true },
  { key: 'labor', name: 'Actual Labor', icon: HardHat, jobScoped: true },
  { key: 'materials', name: 'Actual Materials', icon: Package, jobScoped: true },
  { key: 'equipment', name: 'Equipment', icon: Wrench, jobScoped: true },
  { key: 'change_orders', name: 'Change Orders', icon: FileEdit, jobScoped: true },
  { key: 'overhead', name: 'Overhead Tracker', icon: Building2, jobScoped: false },
  { key: 'burden_rate', name: 'Burden Rate', icon: Scale, jobScoped: false },
  { key: 'profit', name: 'Profit Dashboard', icon: TrendingUp, jobScoped: true },
];

export const FinancialOS = () => {
  const { api, isCOO } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [visibility, setVisibility] = useState({});
  const [activeTab, setActiveTab] = useState('estimate');
  const [showVisModal, setShowVisModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const readOnly = !isCOO;

  useEffect(() => {
    const init = async () => {
      try {
        const [jobsRes, visRes] = await Promise.all([
          api.get('/jobs/'),
          api.get('/accounting/fos/visibility')
        ]);
        setJobs(jobsRes.data || []);
        if (jobsRes.data && jobsRes.data.length) setSelectedJob(jobsRes.data[0].id);
        setVisibility(visRes.data || {});
        if (!isCOO) {
          const firstVisible = ALL_TABS.find(t => visRes.data?.[t.key]);
          if (firstVisible) setActiveTab(firstVisible.key);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [api, isCOO]);

  const tabs = isCOO ? ALL_TABS : ALL_TABS.filter(t => visibility[t.key]);

  if (loading) {
    return <div className="p-8 text-gray-500" data-testid="fos-loading">Loading Financial OS...</div>;
  }

  if (!isCOO && tabs.length === 0) {
    return (
      <div className="p-8" data-testid="fos-no-access">
        <div className="max-w-md mx-auto text-center mt-20">
          <Lock className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-800">No Financial OS Access</h2>
          <p className="text-gray-500 mt-2">The COO has not granted you access to any financial sections yet.</p>
        </div>
      </div>
    );
  }

  const currentTab = tabs.find(t => t.key === activeTab) || tabs[0];

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="financial-os-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Operating System</h1>
          <p className="text-gray-500 mt-1">Estimating, costing, burden &amp; profitability for Jax Remodeling</p>
        </div>
        {isCOO && (
          <button
            data-testid="fos-visibility-settings-btn"
            onClick={() => setShowVisModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Settings className="w-4 h-4" /> Walter Access
          </button>
        )}
      </div>

      {readOnly && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="fos-readonly-banner">
          <Lock className="w-4 h-4" /> Read-only view granted by the COO.
        </div>
      )}

      <div className="flex gap-1 overflow-x-auto border-b border-gray-200 mb-6">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`fos-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === t.key
                  ? 'border-[#C6A87C] text-[#0A0A0A]'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.name}
            </button>
          );
        })}
      </div>

      {currentTab?.jobScoped && (
        <div className="mb-6 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Project:</label>
          <select
            data-testid="fos-job-selector"
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[240px] focus:ring-2 focus:ring-[#C6A87C] focus:outline-none"
          >
            {jobs.length === 0 && <option value="">No jobs found</option>}
            {jobs.map(j => (
              <option key={j.id} value={j.id}>{j.client_name} {j.address ? `— ${j.address}` : ''}</option>
            ))}
          </select>
        </div>
      )}

      <motion.div key={activeTab + selectedJob} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        {activeTab === 'estimate' && <EstimateTab api={api} jobId={selectedJob} readOnly={readOnly} />}
        {activeTab === 'labor' && <LaborTab api={api} jobId={selectedJob} readOnly={readOnly} />}
        {activeTab === 'materials' && <MaterialsTab api={api} jobId={selectedJob} readOnly={readOnly} />}
        {activeTab === 'equipment' && <EquipmentTab api={api} jobId={selectedJob} readOnly={readOnly} />}
        {activeTab === 'change_orders' && <ChangeOrdersTab api={api} jobId={selectedJob} readOnly={readOnly} />}
        {activeTab === 'overhead' && <OverheadTab api={api} readOnly={readOnly} />}
        {activeTab === 'burden_rate' && <BurdenRateTab api={api} readOnly={readOnly} />}
        {activeTab === 'profit' && <ProfitTab api={api} jobId={selectedJob} readOnly={readOnly} />}
      </motion.div>

      {showVisModal && (
        <VisibilityModal
          api={api}
          visibility={visibility}
          onClose={() => setShowVisModal(false)}
          onSaved={(v) => { setVisibility(v); setShowVisModal(false); }}
        />
      )}
    </div>
  );
};

export default FinancialOS;
